import React, { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import LabelImportant from '@material-ui/icons/LabelImportant';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import { SidebarThemeToggle } from './SidebarThemeToggle';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import AppsIcon from '@mui/icons-material/Apps';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import { Administration } from '@backstage-community/plugin-rbac';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import NotificationsIcon from '@mui/icons-material/Notifications';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthOpen,
    height: 88,
    display: 'flex',
    alignItems: 'center',
    padding: '24px 16px 16px 20px',
    marginBottom: 12,
  },
  rootClosed: {
    width: sidebarConfig.drawerWidthClosed,
    justifyContent: 'center',
    padding: '24px 0 16px',
  },
  link: {
    display: 'flex',
    alignItems: 'center',
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div
      className={`${classes.root} ${isOpen ? '' : classes.rootClosed}`}
    >
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

const useSectionLabelStyles = makeStyles(theme => ({
  label: {
    padding: '14px 20px 4px 20px',
    fontSize: '0.625rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    // Theme-aware so it reads on the light sidebar (light mode) and the
    // near-black sidebar (dark mode) alike.
    color: theme.palette.text.disabled,
    userSelect: 'none',
  },
}));

/** Small-caps section heading, hidden while the sidebar is collapsed. */
const SidebarSectionLabel = ({ children }: PropsWithChildren<{}>) => {
  const classes = useSectionLabelStyles();
  const { isOpen } = useSidebarOpenState();

  if (!isOpen) {
    return null;
  }
  return <div className={classes.label}>{children}</div>;
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        <SidebarSectionLabel>Platform</SidebarSectionLabel>
        <SidebarItem icon={HomeIcon} to="/" text="Home" />
        <SidebarItem icon={AppsIcon} to="catalog" text="Service Catalog" />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        <SidebarItem
          icon={CreateComponentIcon}
          to="create"
          text="App Templates"
        />
        <SidebarSectionLabel>Operations</SidebarSectionLabel>
        <SidebarItem icon={LabelImportant} to="requests" text="Your Requests" />
        <SidebarItem icon={Diversity3Icon} to="self-service" text="Self Service" />
        <SidebarItem icon={TrackChangesIcon} to="sip" text="Infra Pilot" />
        <MyGroupsSidebarItem
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={GroupIcon}
        />
        <Administration />
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <SidebarItem icon={NotificationsIcon} to="requests" text="Notifications" />
      <SidebarThemeToggle />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
    {children}
  </SidebarPage>
);
